import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, endpoints, joinUrl } from '../api/config';
import axios from 'axios';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import { BUTTON_STYLE } from '../constants/styles';
import jyBlackLogo from '../assets/JY BLACK.png';

const IntermediateFeature = () => {
  const [loading, setLoading] = useState(true);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [showTwoFactorDialog, setShowTwoFactorDialog] = useState(false);
  const [twoFactorSecret, setTwoFactorSecret] = useState('');
  const [twoFactorQrCode, setTwoFactorQrCode] = useState('');
  const [error, setError] = useState('');
  const [twoFactorError, setTwoFactorError] = useState('');
  const navigate = useNavigate();
  const { logout, login } = useAuth();

  useEffect(() => {
    // Check if user is coming from login and needs 2FA setup
    const username = sessionStorage.getItem('username');
    const tempPassword = sessionStorage.getItem('tempPassword');
    const token = sessionStorage.getItem('token');
    
    // If user has username and password but no token, they're coming from login for 2FA setup
    if (username && tempPassword && !token) {
      checkTwoFactorStatus();
    } else if (!username) {
      // No username found, redirect to login
      navigate('/login', { replace: true });
    } else {
      // User is already authenticated, check 2FA status
      checkTwoFactorStatus();
    }
  }, []);

  const checkTwoFactorStatus = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Debug: Log the API URL being used
      console.log('API Base URL:', import.meta.env.VITE_API_URL);
      console.log('Full URL:', joinUrl(import.meta.env.VITE_API_URL, "/auth/check-2fa-status"));
      
      // Get username from session storage
      const username = sessionStorage.getItem('username');
      if (!username) {
        setError('Username not found. Please log in again.');
        setLoading(false);
        return;
      }
      
      // Get token from sessionStorage and configure headers
      const token = sessionStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Send request with authorization headers if present
      const response = await axios.post(joinUrl(import.meta.env.VITE_API_URL, "/auth/check-2fa-status"), {
        username: username
      }, { headers });
      
      console.log('2FA Status Response:', response.data);
      
      // Check the correct response structure
      const twoFactorEnabled = response.data.data?.two_factor_enabled;
      const requires2fa = response.data.data?.requires_2fa;
      
      console.log('two_factor_enabled:', twoFactorEnabled);
      console.log('requires_2fa:', requires2fa);
      
      // If 2FA is not enabled, set it up immediately
      if (twoFactorEnabled === false) {
        // 2FA is not enabled, set it up immediately
        await setupTwoFactor();
      } else {
        // 2FA is already enabled, redirect to 2FA login page
        // Don't clear credentials yet, they're needed for 2FA login
        window.location.href = '/2fa-login';
      }
    } catch (err) {
      console.error('2FA status check error:', err);
      
      // Log detailed error information to console
      console.group('2FA Status Check Failure Details');
      console.error('Error Message:', err.message);
      if (err.response) {
        console.error('HTTP Status:', err.response.status);
        console.error('Response Headers:', err.response.headers);
        console.error('Response Body:', err.response.data);
      }
      console.groupEnd();
      
      // More detailed error logging for user UI
      if (err.code === 'ERR_NETWORK') {
        setError('Network error: Unable to connect to the server. The 2FA endpoints may not be available yet. You can skip 2FA setup for now.');
      } else if (err.response?.status === 404) {
        setError('2FA endpoints not found. The 2FA feature may not be implemented on the backend yet.');
      } else if (err.response?.status === 401) {
        setError('Authentication error. Please log in again.');
      } else if (err.response?.status === 422) {
        setError('Invalid request format. Please check the username and try again.');
      } else {
        const errorDetail = err.response?.data?.detail || err.response?.data?.message || err.message || 'Unknown error';
        setError(`Failed to check 2FA status: ${errorDetail}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const setupTwoFactor = async () => {
    try {
      setTwoFactorLoading(true);
      setError('');
      
      // Get username and password from session storage
      const username = sessionStorage.getItem('username');
      const tempPassword = sessionStorage.getItem('tempPassword');
      
      if (!username || !tempPassword) {
        setError('Authentication data not found. Please log in again.');
        return;
      }
      
      const requestBody = {
        username: username,
        password: tempPassword
      };
      
      console.log('2FA Setup Request:', { username, password: '***' });
      
      // Get token from sessionStorage and configure headers
      const token = sessionStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.post(joinUrl(import.meta.env.VITE_API_URL, "/2fa/setup"), requestBody, { headers });
      
      console.log('2FA Setup Response:', response.data);
      
      // Check for the correct response structure
      const qrCodeUrl = response.data.data?.qr_code_url;
      const secretKey = response.data.data?.secret_key;
      const setupInstructions = response.data.data?.setup_instructions;
      
      console.log('QR Code URL:', qrCodeUrl);
      console.log('Secret Key:', secretKey);
      console.log('Setup Instructions:', setupInstructions);
      console.log('QR Code URL exists:', !!qrCodeUrl);
      console.log('Secret Key exists:', !!secretKey);
      
      if (qrCodeUrl && secretKey) {
        console.log('Setting up 2FA dialog with QR code and secret');
        setTwoFactorSecret(secretKey);
        setTwoFactorQrCode(qrCodeUrl);
        setShowTwoFactorDialog(true);
      } else {
        console.log('Validation failed - missing QR code or secret');
        console.log('qrCodeUrl type:', typeof qrCodeUrl);
        console.log('secretKey type:', typeof secretKey);
        setError('Failed to get 2FA setup information. Response format may be different than expected.');
      }
    } catch (err) {
      console.error('2FA setup error:', err);
      
      // Log detailed error information to console
      console.group('2FA Setup Failure Details');
      console.error('Error Message:', err.message);
      if (err.response) {
        console.error('HTTP Status:', err.response.status);
        console.error('Response Headers:', err.response.headers);
        console.error('Response Body:', err.response.data);
      }
      console.groupEnd();
      
      if (err.response?.status === 404) {
        setError('2FA setup endpoint not found. Please check with your backend team.');
      } else if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else {
        const errorDetail = err.response?.data?.detail || err.response?.data?.message || err.message || 'Unknown error';
        setError(`Failed to setup 2FA: ${errorDetail}`);
      }
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const verifyTwoFactorCode = async () => {
    if (!twoFactorCode.trim()) {
      setTwoFactorError('Please enter the verification code.');
      return;
    }

    try {
      setTwoFactorLoading(true);
      setTwoFactorError('');
      
      // Get username from session storage
      const username = sessionStorage.getItem('username');
      if (!username) {
        setTwoFactorError('Username not found. Please log in again.');
        return;
      }
      
      // Prepare the request body for 2FA enable
      const requestBody = {
        username: username,
        secret_key: twoFactorSecret,
        totp_code: twoFactorCode
      };
      
      console.log('2FA Enable Request Body:', requestBody);
      console.log('2FA Enable Request URL:', joinUrl(import.meta.env.VITE_API_URL, "/2fa/enable"));
      console.log('Username:', username);
      console.log('Secret Key:', twoFactorSecret);
      console.log('TOTP Code:', twoFactorCode);
      
      // Get token from sessionStorage and configure headers
      const token = sessionStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.post(joinUrl(import.meta.env.VITE_API_URL, "/2fa/enable"), requestBody, { headers });
      
      console.log('2FA Enable Response:', response.data);
      
      if (response.data.status === 200 || response.data.success) {
        // 2FA setup successful
        setShowTwoFactorDialog(false);
        sessionStorage.setItem('intermediateFeatureCompleted', 'true');
        
        // Store tempPassword before clearing it
        const storedPassword = sessionStorage.getItem('tempPassword');
        
        // Clear temporary password after successful verification
        sessionStorage.removeItem('tempPassword');
        
        // Set 2FA as verified
        sessionStorage.setItem('twoFactorVerified', 'true');
        
        // Check if the response contains an access token
        if (response.data.access_token) {
          // Use the token from the response
          login(response.data.access_token, { username }, true);
        } else {
          // If no token in response, we need to call login API with 2FA
          try {
            const loginResponse = await axios.post(joinUrl(import.meta.env.VITE_API_URL, "/admin/login-with-2fa"), {
              username: username,
              password: storedPassword || '',
              totp_code: twoFactorCode
            });
            
            if (loginResponse.data.access_token) {
              login(loginResponse.data.access_token, { username }, true);
            }
          } catch (loginErr) {
            console.error('Login after 2FA setup failed:', loginErr);
            // Continue anyway, the user can log in manually
          }
        }
        
        // Navigate directly to dashboard using window.location to force app reload
        window.location.href = '/dashboard';
      } else {
        setTwoFactorError('Invalid verification code. Please try again.');
      }
    } catch (err) {
      console.error('2FA verification error:', err);
      
      // Log detailed error information to console
      console.group('2FA Verification Failure Details');
      console.error('Error Message:', err.message);
      if (err.response) {
        console.error('HTTP Status:', err.response.status);
        console.error('Response Headers:', err.response.headers);
        console.error('Response Body:', err.response.data);
      }
      console.groupEnd();
      
      if (err.response?.status === 404) {
        setTwoFactorError('2FA enable endpoint not found. Please check with your backend team.');
      } else if (err.response?.status === 400) {
        setTwoFactorError('Invalid verification code. Please try again.');
      } else if (err.response?.status === 401) {
        setTwoFactorError('Authentication failed. Please try again.');
      } else if (err.response?.status === 422) {
        // Handle validation errors
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail) && errorDetail.length > 0) {
          console.error('Validation error details:', errorDetail);
          const firstError = errorDetail[0];
          if (firstError.msg) {
            setTwoFactorError(`Validation error: ${firstError.msg}`);
          } else if (typeof firstError === 'string') {
            setTwoFactorError(`Validation error: ${firstError}`);
          } else {
            setTwoFactorError('Invalid request format. Please check the verification code and try again.');
          }
        } else if (errorDetail) {
          setTwoFactorError(`Validation error: ${errorDetail}`);
        } else {
          setTwoFactorError('Invalid request format. Please check the verification code and try again.');
        }
      } else {
        setTwoFactorError(`Failed to verify code: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleContinue = async () => {
    // This function is now replaced by the 2FA flow
    // The continue button will only be shown if there's an error
    await checkTwoFactorStatus();
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #e3f0ff 0%, #fafcff 100%)',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={6}
            sx={{
              p: 5,
              borderRadius: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: 'white',
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
            }}
          >
            <img src={jyBlackLogo} alt="JY Logo" style={{ width: 90, paddingBottom: 20 }} />
            <CircularProgress size={40} sx={{ my: 3 }} />
            <Typography variant="h6" color="text.secondary">
              Setting up Two-Factor Authentication...
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #e3f0ff 0%, #fafcff 100%)',
        position: 'relative',
      }}
    >
      <Button
        variant="outlined"
        onClick={handleLogout}
        sx={{ 
          position: 'absolute',
          top: 20,
          right: 20,
          py: 1,
          fontWeight: 600,
          fontSize: 14,
          letterSpacing: 1,
          ...BUTTON_STYLE 
        }}
      >
        Logout
      </Button>
      
      <Container maxWidth="md">
        <Paper
          elevation={6}
          sx={{
            p: 5,
            borderRadius: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'white',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
          }}
        >
          <img src={jyBlackLogo} alt="JY Logo" style={{ width: 90, paddingBottom: 20 }} />
          
          <Typography component="h1" variant="h4" sx={{ mb: 3, color: 'text.primary', fontWeight: 600, textAlign: 'center' }}>
            Two-Factor Authentication Setup
          </Typography>
          
          <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary', textAlign: 'center', maxWidth: 600 }}>
            To ensure the security of your account, we need to set up two-factor authentication.
            This adds an extra layer of protection to your login process.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              onClick={handleContinue}
              variant="outlined"
              disabled={twoFactorLoading}
              sx={{ 
                py: 1.5,
                px: 4,
                fontWeight: 600,
                fontSize: 16,
                letterSpacing: 1,
                ...BUTTON_STYLE 
              }}
            >
              {twoFactorLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Processing...
                </>
              ) : (
                'Retry Setup'
              )}
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* 2FA Setup Dialog */}
      <Dialog 
        open={showTwoFactorDialog} 
        onClose={() => {}} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 600 }}>
          Set Up Two-Factor Authentication
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):
            </Typography>
            {twoFactorQrCode && (
              <Box sx={{ mb: 3 }}>
                <img 
                  src={twoFactorQrCode} 
                  alt="2FA QR Code" 
                  style={{ 
                    maxWidth: '200px', 
                    height: 'auto',
                    border: '1px solid #ddd',
                    borderRadius: '8px'
                  }} 
                />
              </Box>
            )}
            <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace', backgroundColor: '#f5f5f5', p: 1, borderRadius: 1 }}>
              Secret: {twoFactorSecret}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              If you can't scan the QR code, enter the secret manually in your authenticator app.
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Verification Code"
            value={twoFactorCode}
            onChange={(e) => setTwoFactorCode(e.target.value)}
            placeholder="Enter 6-digit code from your app"
            sx={{ mb: 2 }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                verifyTwoFactorCode();
              }
            }}
          />

          {twoFactorError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {twoFactorError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={verifyTwoFactorCode}
            variant="contained"
            disabled={twoFactorLoading || !twoFactorCode.trim()}
            fullWidth
            sx={{ 
              py: 1.5,
              fontWeight: 600,
              fontSize: 16,
              ...BUTTON_STYLE 
            }}
          >
            {twoFactorLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                Verifying...
              </>
            ) : (
              'Verify and Complete Setup'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntermediateFeature; 
